import { t as uiText } from '../../i18n';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { storage } from '../../lib/firebase';

/**
 * 外部photoURLから画像を取得してStorageに保存する
 * @param photoURL 外部の画像URL
 * @param userId ユーザーID
 * @returns Storageパス（失敗時はnull）
 */
export const syncPhotoUrlToStorage = async (
  photoURL: string, 
  userId: string
): Promise<string | null> => {
  try {
    // 外部URLから画像を取得
    const response = await fetch(photoURL);
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    const blob = await response.blob();

    // 画像ファイルかどうかチェック
    if (!blob.type.startsWith('image/')) {
      throw new Error(uiText("Not an image file: {{type}}", { type: blob.type }));
    }

    // ファイル名を生成（拡張子を推測）
    const extension = getImageExtension(blob.type);
    const timestamp = Date.now();
    const fileName = `userIcons/${userId}/${timestamp}_profile.${extension}`;

    // Storageにアップロード
    const storageRef = ref(storage, fileName);
    const snapshot = await uploadBytes(storageRef, blob);

    return fileName;
  } catch (error) {
    console.error('[PhotoSync] 外部photoURL同期失敗', { 
      userId, 
      photoURL: photoURL.substring(0, 100), 
      error: error instanceof Error ? error.message : String(error)
    });
    return null;
  }
};

/**
 * 画像のMIMEタイプから拡張子を取得
 */
const getImageExtension = (mimeType: string): string => {
  switch (mimeType) {
    case 'image/jpeg':
      return 'jpg';
    case 'image/png':
      return 'png';
    case 'image/gif':
      return 'gif';
    case 'image/webp':
      return 'webp';
    case 'image/svg+xml':
      return 'svg';
    default:
      return 'jpg'; // デフォルトはjpg
  }
};

/**
 * 外部photoURLが同期対象かどうかを判定
 * @param photoURL 外部の画像URL
 * @returns 同期対象かどうか
 */
export const shouldSyncPhotoUrl = (photoURL: string): boolean => {
  if (!photoURL) return false;
  
  // 同期対象のドメイン
  const syncTargetDomains = [
    'googleusercontent.com',
    'googleapis.com', 
    'appleid.apple.com',
    'gravatar.com'
  ];
  
  return syncTargetDomains.some(domain => photoURL.includes(domain));
};

/**
 * 既存ユーザーのphotoPath同期処理
 * @param userId ユーザーID
 * @param photoURL 外部の画像URL
 * @param currentPhotoPath 現在のphotoPath（nullの場合は同期対象）
 * @returns 更新されたphotoPath（同期失敗時はnull）
 */
export const syncExistingUserPhoto = async (
  userId: string,
  photoURL: string | null,
  currentPhotoPath: string | null
): Promise<string | null> => {
  // photoPathが既に存在する場合はスキップ
  if (currentPhotoPath) {
    return currentPhotoPath;
  }

  // photoURLがない場合はスキップ
  if (!photoURL || !shouldSyncPhotoUrl(photoURL)) {
    return null;
  }

  try {
    const photoPath = await syncPhotoUrlToStorage(photoURL, userId);

    if (photoPath) {}

    return photoPath;
  } catch (error) {
    console.warn('[PhotoSync] 既存ユーザーphotoPath同期失敗', { 
      userId, 
      photoURL: photoURL.substring(0, 100), 
      error: error instanceof Error ? error.message : String(error)
    });
    return null;
  }
};
